import { z } from 'zod';
/**
 * Área de transferência e transferência de arquivos, no DataChannel "input" (JSON)
 * e no DataChannel "transfer" (bytes). O Host cria os dois.
 *
 * Os bytes de uma transferência são a concatenação dos arquivos, na ordem da
 * oferta, em pedaços de no máximo TRANSFER_CHUNK_BYTES. O fim de cada arquivo
 * vem do `size` da oferta, não do tamanho da mensagem. Uma direção por vez.
 * Quem envia espera quando o canal acumula TRANSFER_BUFFER_HIGH_BYTES.
 * Bytes podem chegar antes do JSON da oferta; guarde até TRANSFER_EARLY_BYTES.
 */
export const TRANSFER_CHANNEL_LABEL = 'transfer';
export const TRANSFER_CHUNK_BYTES = 16 * 1024;
export const TRANSFER_BUFFER_HIGH_BYTES = 256 * 1024;
export const TRANSFER_BUFFER_LOW_BYTES = TRANSFER_BUFFER_HIGH_BYTES / 2;
export const TRANSFER_EARLY_BYTES = 1024 * 1024;
export const CLIPBOARD_TEXT_MAX_BYTES = 1024 * 1024;
/**
 * Unidades UTF-16 por mensagem `clipboard_text`. Mesmo com todo caractere escapado
 * no JSON (6 bytes), a parte fica abaixo dos 64 KiB que o SCTP aceita por padrão.
 */
export const CLIPBOARD_TEXT_PART_CHARS = 8 * 1024;
/** Imagem da área de transferência, já em PNG. Vai pelo canal "transfer", como um arquivo. */
export const CLIPBOARD_IMAGE_MAX_BYTES = 64 * 1024 * 1024;
export const MAX_TRANSFER_FILES = 500;
export const MAX_TRANSFER_ID_LEN = 64;
/** 32 GiB. Cabe com exatidão num número do JavaScript. */
export const MAX_FILE_BYTES = 32 * 1024 * 1024 * 1024;
const RESERVED = /^(con|prn|aux|nul|com\d|lpt\d)$/i;
/** Nome de arquivo solto, sem pasta. A mesma regra vale no Host. */
export function isSafeFileName(name) {
    if (name.length === 0 || name.length > 255)
        return false;
    if (name === '.' || name === '..')
        return false;
    if (name.endsWith('.') || name.endsWith(' '))
        return false;
    if (/[<>:"/\\|?*]/.test(name))
        return false;
    for (let i = 0; i < name.length; i++) {
        if (name.charCodeAt(i) < 32)
            return false;
    }
    const stem = name.split('.')[0] ?? name;
    return !RESERVED.test(stem);
}
export function clipboardTextFits(text) {
    return new TextEncoder().encode(text).byteLength <= CLIPBOARD_TEXT_MAX_BYTES;
}
/** Partes de `clipboard_text`, na ordem. Não separa um par substituto (emoji). */
export function splitClipboardText(text) {
    const parts = [];
    let start = 0;
    while (text.length - start > CLIPBOARD_TEXT_PART_CHARS) {
        let end = start + CLIPBOARD_TEXT_PART_CHARS;
        const last = text.charCodeAt(end - 1);
        if (last >= 0xd800 && last <= 0xdbff)
            end -= 1;
        parts.push(text.slice(start, end));
        start = end;
    }
    parts.push(text.slice(start));
    return parts;
}
export const TransferFile = z.object({
    name: z.string().refine(isSafeFileName, 'nome de arquivo inválido'),
    size: z.number().int().nonnegative().max(MAX_FILE_BYTES),
});
const transferId = z.string().min(1).max(MAX_TRANSFER_ID_LEN);
const fileList = z.array(TransferFile).min(1).max(MAX_TRANSFER_FILES);
/**
 * Viewer -> Host. Coloca o texto na área de transferência; se `paste`, o Host cola com Ctrl+V.
 * Texto longo vai em partes (`splitClipboardText`): `more` nas anteriores, ausente na última.
 */
export const ViewerClipboardText = z.object({
    type: z.literal('clipboard_text'),
    text: z.string().refine(clipboardTextFits, 'texto grande demais'),
    paste: z.boolean(),
    more: z.boolean().optional(),
});
/** Viewer -> Host. Os bytes seguem no canal "transfer". */
export const ViewerFilesOffer = z.object({
    type: z.literal('files_offer'),
    transferId,
    files: fileList,
    /**
     * true: grava na pasta temporária de quem está logado, coloca os arquivos na
     * área de transferência (cópia) e cola. false: grava na Área de Trabalho, sem
     * mexer na área de transferência.
     */
    paste: z.boolean(),
});
/**
 * Viewer -> Host. Imagem da área de transferência (PNG) para colar: os `size` bytes seguem no
 * canal "transfer". O Host a coloca na área de transferência como imagem e cola com Ctrl+V.
 */
export const ViewerClipboardImage = z.object({
    type: z.literal('clipboard_image'),
    transferId,
    size: z.number().int().positive().max(CLIPBOARD_IMAGE_MAX_BYTES),
});
/**
 * Viewer -> Host. Pede a imagem anunciada em `clipboard_image`. O Host responde com um
 * `files_offer` de um arquivo só (o PNG) e os bytes no canal "transfer".
 */
export const ImageRequest = z.object({
    type: z.literal('image_request'),
    clipboardId: transferId,
    transferId,
});
/**
 * Viewer -> Host. Pede um arquivo da lista anunciada em `clipboard_files`.
 * O Host transmite só esse arquivo no canal "transfer".
 */
export const FileRequest = z.object({
    type: z.literal('file_request'),
    clipboardId: transferId,
    index: z.number().int().nonnegative().max(MAX_TRANSFER_FILES - 1),
    transferId,
});
export const TransferDone = z.object({
    type: z.literal('transfer_done'),
    transferId,
});
export const TransferError = z.object({
    type: z.literal('transfer_error'),
    transferId,
    message: z.string().min(1).max(400),
});
/** Host -> Viewer. O texto da área de transferência remota mudou. Partes como em `ViewerClipboardText`. */
export const HostClipboardText = z.object({
    type: z.literal('clipboard_text'),
    text: z.string().refine(clipboardTextFits, 'texto grande demais'),
    more: z.boolean().optional(),
});
/**
 * Host -> Viewer. A área de transferência remota tem arquivos.
 * Só entram arquivos comuns do primeiro nível; pastas aumentam `skippedFolders`.
 */
export const HostClipboardFiles = z.object({
    type: z.literal('clipboard_files'),
    /** Lista congelada no Host neste aviso. Um pedido usa este id e o índice. */
    clipboardId: transferId,
    files: z.array(TransferFile).max(MAX_TRANSFER_FILES),
    skippedFolders: z.number().int().nonnegative(),
});
/** Host -> Viewer. A área de transferência remota tem uma imagem; `size` é o tamanho do PNG. */
export const HostClipboardImage = z.object({
    type: z.literal('clipboard_image'),
    /** Imagem congelada no Host neste aviso. O pedido (`image_request`) usa este id. */
    clipboardId: transferId,
    size: z.number().int().positive().max(CLIPBOARD_IMAGE_MAX_BYTES),
});
/** Host -> Viewer. Oferta dos arquivos que o Host vai mandar no canal "transfer". */
export const HostFilesOffer = z.object({
    type: z.literal('files_offer'),
    transferId,
    files: fileList,
});
//# sourceMappingURL=transfer.js.map